import { useState } from "react";
import "./RefreshButton.css";

/**
 * Generic local refresh button. Calls `onClick` and shows a spinning state
 * for the duration of the (sync or async) handler.
 *
 *   <RefreshButton onClick={fetchMemories} title="Refresh memories" />
 *
 * Use one per page so a refresh only re-fetches that page's data, never the
 * whole window. Never call `window.location.reload()` from `onClick`.
 */
export default function RefreshButton({
  onClick,
  title = "Refresh",
  className = "",
  disabled = false,
  size = 16,
  busy: externalBusy,
}) {
  const [internalBusy, setInternalBusy] = useState(false);
  const busy = externalBusy ?? internalBusy;

  const handle = async (e) => {
    e?.stopPropagation?.();
    if (busy || disabled || !onClick) return;
    let result;
    try {
      result = onClick(e);
    } catch (err) {
      console.error("RefreshButton sync error:", err);
      return;
    }
    if (result && typeof result.then === "function") {
      setInternalBusy(true);
      try {
        await result;
      } catch (err) {
        console.error("RefreshButton async error:", err);
      } finally {
        setInternalBusy(false);
      }
    }
  };

  return (
    <button
      type="button"
      className={`refresh-btn ${busy ? "spinning" : ""} ${className}`}
      onClick={handle}
      title={title}
      aria-label={title}
      disabled={disabled || busy}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="23 4 23 10 17 10" />
        <polyline points="1 20 1 14 7 14" />
        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" />
        <path d="M20.49 15A9 9 0 0 1 5.64 18.36L1 14" />
      </svg>
    </button>
  );
}
